import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { withRouter, Redirect } from 'react-router-dom';
import ReactDOM from 'react-dom';
import { Row, Grid, Col } from '@youngcapital/component-library';
import i18next from 'i18next';
import { includes } from 'lodash';
import classNames from 'classnames';

import { BLOKKENDOOS_SELECTORS } from '../../services/constants';
import EmailNotUnique from '../EmailNotUnique';

import withTranslation from '../helpers/withTranslation';
import {
  makeSerializer,
  DEFAULT_SERIALIZERS
} from '../../helpers/formSerializer';
import { clearLocalStorage } from '../fieldsets/CvFieldset/helpers';
import SignupForm from '../SignupForm';
import Header from '../Header';
import Sidebar from '../Sidebar';
import Navigator from '../../services/navigator';
import FieldsetsGenerator from '../fieldsets/fieldsetsGenerator';
import * as fieldsetComponents from '../fieldsets';

import { AppContext } from '../../AppContext';
import CandidateService from '../../services/candidateService';

const TYPES_TO_OMIT_WHEN_AUTHED = ['password'];

const cache = new Map();
class Page extends PureComponent {
  // eslint-disable-next-line react/static-property-placement
  static contextType = AppContext;

  static omitFieldsetsOfType = (fieldsets, typesToOmit = []) => {
    const filteredFieldsets = fieldsets.filter(
      fieldset => !includes(typesToOmit, fieldset.type)
    );
    return filteredFieldsets;
  };

  constructor(props, context) {
    super(props, context);

    const { routes, history } = this.props;
    const { pageConfig, blokkendoos, currentPageIndex, siteAbbr } = context;
    const { fieldsets } = pageConfig;

    this.currentPageIndex = currentPageIndex;
    this.siteAbbr = siteAbbr;

    this.nav = new Navigator({
      routes,
      history
    });

    this.api = new CandidateService({
      cache,
      serializer: makeSerializer(DEFAULT_SERIALIZERS),
      isMobileApp: blokkendoos.isMobileApp()
    });

    this.fieldsets = new FieldsetsGenerator(
      this.api.isAuthenticated
        ? Page.omitFieldsetsOfType(fieldsets, TYPES_TO_OMIT_WHEN_AUTHED)
        : fieldsets,
      fieldsetComponents
    );

    this.state = {
      errorMessage: ''
    };

    window.signupSPALoaded = true;
    const trackingEvent = new CustomEvent('SignupSPALoaded');
    document.dispatchEvent(trackingEvent);
    blokkendoos.deleteLoader();
  }

  get shouldRedirect() {
    const noRedirectQueryPassed =
      window.location.search.includes('no_redirect');
    return (
      !noRedirectQueryPassed &&
      !this.api.isAuthenticated &&
      this.currentPageIndex !== 0
    );
  }

  componentDidMount = () => {
    /* We have to load content inside an existing BLK modal */
    window.i18next = i18next;
    const modal = document.querySelector(
      BLOKKENDOOS_SELECTORS.CANDIDATE_MODAL_FLASH_MESSAGE
    );
    if (modal) {
      ReactDOM.render(<EmailNotUnique />, modal);
    }
  };

  clearError = () => this.setState({ errorMessage: '' });

  onSubmit = (values, actions) => {
    this.clearError();
    const updatedValues = this.updateValues(values);
    const { t } = this.props;

    this.api
      .save(updatedValues)
      .then(clearLocalStorage)
      .then(this.nextPage)
      .catch(err => {
        let errorMessage = t('api_error_message');
        if (err.isInternal) {
          errorMessage = err.message;
        }

        this.setError(errorMessage);
        actions.setSubmitting(false);
        actions.resetForm();
      });
  };

  nextPage = () => {
    if (this.nav.isLastPage()) {
      return this.goToThankYouPage();
    }
    return this.nav.nextPage();
  };

  goToThankYouPage = () => {
    window.location = this.api.urlAddition;
  };

  setError = errorMessage => this.setState({ errorMessage });

  formValidationError = () => {
    const { t } = this.props;
    this.setState({ errorMessage: t('form.validation_error') });
    window.scrollTo(0, 0);
  };

  fetchAddress = ({ number, zipcode }) =>
    this.api.fetchNlAddress({ number, zipcode });

  fetchEducationsList = (manualInput, selectedEduTypeId) =>
    this.api.fetchEducationsList(manualInput, selectedEduTypeId);

  updateValues(newValues) {
    const { setValues, values } = this.props;
    const updatedValues = { ...values, ...newValues };
    setValues({ ...values, ...updatedValues });
    return updatedValues;
  }

  render() {
    const { values } = this.props;
    const { Fieldsets, initialValues, validationSchema } = this.fieldsets;
    const isNext = this.siteAbbr === 'NEXT';

    return this.shouldRedirect ? (
      <Redirect exact to='/' />
    ) : (
      <>
        <Header
          errorMessage={this.state.errorMessage}
          onClose={this.clearError}
        />
        <Grid
          container
          fluid
          className={classNames('pb-10x card--top', isNext && 'next')}
        >
          <Row>
            <Col xs={12} md={4} className='mt-4x mt-md-0' first={['md']}>
              <Sidebar />
            </Col>
            <Col xs={12} md={8} first={['xs']} className='mt-md-7x'>
              <SignupForm
                onSubmit={this.onSubmit}
                initialValues={{ ...initialValues, ...values }}
                validationSchema={validationSchema}
                formValidationError={this.formValidationError}
              >
                {form => (
                  <Fieldsets
                    form={form}
                    fetchAddress={this.fetchAddress}
                    fetchEducationsList={this.fetchEducationsList}
                  />
                )}
              </SignupForm>
            </Col>
          </Row>
        </Grid>
      </>
    );
  }
}

Page.propTypes = {
  history: PropTypes.object.isRequired,
  routes: PropTypes.array.isRequired,
  setValues: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  values: PropTypes.object.isRequired
};

export { Page };

export default withTranslation()(withRouter(Page));
